import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { NgClickOutsideDirective } from "ng-click-outside2";

import { ChangeDetectionStrategy, Component, Input, OnChanges, inject, input, output } from "@angular/core";
import { FormsModule } from "@angular/forms";

export interface CustomDropdownOption {
  id: number;
  disabled?: boolean;
  name: string;
  select: boolean;
}

@Component({
  selector: "custom-dropdown",
  templateUrl: "custom-dropdown.html",
  styleUrls: ["custom-dropdown.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, FormsModule, TranslatePipe, NgClickOutsideDirective],
})
export class CustomDropdown implements OnChanges {
  // Note to future self: these are not signals yet, because
  // this component supports changing the options (which can't be done in a signal).
  // A model helps partly, but causes issues as well.
  @Input() multiselect = true;
  @Input() options: any[] = [];
  @Input() formatter: (item: any) => string = (item: CustomDropdownOption) => item.name;
  // when true, the options array is altered by reference, when false, a shadow array is used
  // and the original options remain unchanged.
  @Input() byReference = true;
  searchable = input(true);

  disabled = input<boolean>(false);
  preselectedOptions = input<number[]>([]);
  selectionChanged = output<number[]>();

  public active = false;
  public searchValue = "";
  public shadowOptions: CustomDropdownOption[] = [];
  public focusedIndex = -1;

  private readonly translate = inject(TranslateService);

  get actionableOptions(): CustomDropdownOption[] {
    return this.byReference ? this.options : this.shadowOptions;
  }

  get filteredOptions(): CustomDropdownOption[] {
    return this.actionableOptions.filter((option: CustomDropdownOption) =>
      option.name.toLowerCase().includes(this.searchValue.toLowerCase()),
    );
  }

  get selectedOptions(): string {
    const selectedOptions = this.actionableOptions.filter((option: CustomDropdownOption) => option.select === true);
    const selectedOptionsString = selectedOptions
      .map((item: CustomDropdownOption) => item.name)
      .toString()
      .replace(/,/g, ", ");

    if (selectedOptions.length > 0) {
      return selectedOptionsString;
    }

    return this.actionableOptions.length > 0 ?
        this.translate.instant("Dropdown.NoneSelected")
      : this.translate.instant("Dropdown.NoneAvailable");
  }

  get selectedOptionsCount(): number {
    return this.actionableOptions.filter((item: CustomDropdownOption) => item.select === true).length;
  }

  get selectedOptionsIds(): number[] {
    return this.actionableOptions
      .filter((item: CustomDropdownOption) => item.select)
      .map((item: CustomDropdownOption) => item.id);
  }

  ngOnChanges(): void {
    const haveFormatter = this.formatter !== undefined;
    const haveOptions = this.options.length > 0;

    if (haveOptions) {
      this.shadowOptions = this.options.map((item: CustomDropdownOption) => ({
        id: item.id,
        disabled: item.disabled ?? false,
        name: haveFormatter ? this.formatter(item) : item.name,
        select: this.preselectedOptions().includes(item.id),
      }));
    }
  }

  optionClick(id: number): void {
    const item = this.actionableOptions.find((opt: CustomDropdownOption) => opt.id === id);
    if (!item || item.disabled) return;

    if (!this.multiselect) {
      this.actionableOptions.map((opt) => {
        opt.select = false;
      });
    }

    item.select = !item.select;

    if (!this.multiselect) {
      this.clickedOutside();
    }
  }

  clickInput(): void {
    if (!this.disabled()) this.active = !this.active;
  }

  isSelected(id: number): void {
    const item = this.actionableOptions.find((item: CustomDropdownOption) => item.id === id);
    if (item) {
      item.select = true;
    }
  }

  clickedOutside(): void {
    if (this.active) {
      this.active = false;
      this.focusedIndex = -1;
      this.selectionChanged.emit(this.selectedOptionsIds);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.active) {
          this.active = true;
          this.focusedIndex = 0;
        } else {
          this.focusedIndex = Math.min(this.focusedIndex + 1, this.filteredOptions.length - 1);
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
        break;
      case "Enter":
        event.preventDefault();
        if (!this.active) {
          this.active = true;
          this.focusedIndex = 0;
        } else if (this.focusedIndex >= 0) {
          this.optionClick(this.filteredOptions[this.focusedIndex].id);
        }
        break;
      case "Escape":
        event.preventDefault();
        this.clickedOutside();
        break;
    }
  }
}
